import { useEffect, useMemo, useState } from "react";
import { DatePicker } from "@/components/ui/date-picker";
import {
  ArrowDownUp, Boxes, ChefHat, CheckCircle2, ClipboardList, IndianRupee, LockKeyhole, Plus, ScrollText, TriangleAlert, Edit, TrendingDown
} from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { featureAllowed, screenAllowed } from "@/lib/access";
import { useInventory } from "@/hooks/useInventory";
import { MOVEMENT_DIRECTION, MOVEMENT_TYPES, STOCK_UNITS, listStores, signedQuantity } from "@/lib/inventoryApi";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageShell } from "@/components/PageShell";
import { MONEY_CAPTION, MONEY_GRAND_TOTAL, MONEY_LINE, inr as formatInr } from "@/lib/format";
import { EmptyState, LoadingState } from "@/components/ui/StateBoundaries";
import RecipesPanel from "@/components/inventory/RecipesPanel";
import BuyUnitsEditor from "@/components/inventory/BuyUnitsEditor";
import StockCounts from "@/components/inventory/StockCounts";

const READ_ROLES = ["admin", "cashier"];
const WRITE_ROLES = ["admin"];

/**
 * Money on this page, with the one domain rule the shared formatter cannot know.
 *
 * The local formatter this replaces built its own `₹` + `toLocaleString` string and
 * therefore ignored the configured currency symbol and locale entirely (§4.2). All
 * it is allowed to keep is the em dash: a stock item with no purchase history has
 * an unknown value, and ₹0.00 is a claim about the money (§47). Everything with a
 * number in it goes through inr().
 */
const inr = value =>
  value === null || value === undefined || value === "" ? "—" : formatInr(value);

const qty = value => {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount)
    ? amount.toLocaleString("en-IN", { maximumFractionDigits: 3 })
    : "—";
};

const pad = value => String(value).padStart(2, "0");

const stamp = value => {
  if (!value) return "—";
  const text = String(value);
  const carriesZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(text);
  const at = new Date(carriesZone ? text : `${text}Z`);
  return Number.isNaN(at.getTime())
    ? text.replace("T", " ").slice(0, 16)
    : `${at.toLocaleDateString("en-IN")} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
};

const typeLabel = value => String(value || "").replace(/_/g, " ");

function StockItemDialog({ item, stores = [], token, canWrite, onSave, onClose }) {
  const [form, setForm] = useState({
    name: item?.name || "",
    unit: item?.unit || "",
    department_id: item?.department_id ? String(item.department_id) : "",
    sku: item?.sku || "",
    category: item?.category || "",
    reorder_level: item ? String(item.reorder_level ?? 0) : "0",
    usable_pct: item ? String(item.usable_pct ?? 100) : "100",
    is_active: item ? item.is_active !== false : true,
  });
  const [failure, setFailure] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.unit) return toast.error("Choose the unit stock is kept in");
    const level = Number(form.reorder_level);
    if (!Number.isFinite(level) || level < 0) return toast.error("Reorder level cannot be negative");
    const usable = Number(form.usable_pct);
    if (!Number.isFinite(usable) || usable <= 0 || usable > 100) return toast.error("Usable % must be above 0 and at most 100");

    const body = {
      name: form.name.trim(),
      unit: form.unit,
      department_id: form.department_id ? Number(form.department_id) : null,
      sku: form.sku.trim() || null,
      category: form.category.trim() || null,
      reorder_level: level,
      usable_pct: usable,
      ...(item ? { is_active: form.is_active } : {}),
    };
    setSaving(true);
    const result = await onSave(body);
    setSaving(false);
    if (!result.ok) {
      setFailure(result.error);
      return toast.error(result.error);
    }
    toast.success(`${body.name} saved`);
    onClose();
  };

  return <Modal title={item ? `Edit ${item.name}` : "Add stock item"} onClose={onClose} testId="stock-item-modal" footer={<><Button variant="outline" onClick={onClose} data-testid="save-stock-item-button-cancel">Cancel</Button><Button variant="action" onClick={submit} disabled={saving} data-testid="save-stock-item-button">{item ? "Save changes" : "Add item"}</Button></>}>
    <div className="space-y-4">
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Name</label>
        <Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="stock-item-name-input"/>
      </div>
      <div className="grid grid-cols-2 gap-base">
        <div className="space-y-1.5">
          <label className="text-meta font-medium text-muted-foreground">Stock unit</label>
          {/* The fixed list (owner brief B3): "kg", "Kg" and "kgs" were three units. */}
          <select className="w-full h-11 px-3 border rounded-md bg-card text-body" value={form.unit}
                  onChange={e => set("unit", e.target.value)} data-testid="stock-item-unit-input">
            <option value="">Choose…</option>
            {[...new Set([...STOCK_UNITS, ...(form.unit && !STOCK_UNITS.includes(form.unit) ? [form.unit] : [])])]
              .map(u => <option key={u} value={u}>{u}</option>)}
          </select>
          <p className="text-micro text-muted-foreground">Liquor in ML, hookah flavour in GM; a supplier's bottle or case is a buy unit.</p>
        </div>
        <div className="space-y-1.5">
          <label className="text-meta font-medium text-muted-foreground">Store</label>
          <select className="w-full h-11 px-3 border rounded-md bg-card text-body" value={form.department_id}
                  onChange={e => set("department_id", e.target.value)} data-testid="stock-item-store-select">
            <option value="">No store</option>
            {stores.filter(d => d.is_active !== false || String(d.id) === form.department_id)
              .map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">SKU</label>
        <Input value={form.sku} onChange={e => set("sku", e.target.value)} placeholder="optional, must be unique" data-testid="stock-item-sku-input"/>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Category</label>
        <Input value={form.category} onChange={e => set("category", e.target.value)} placeholder="Dairy, Produce, Bar" data-testid="stock-item-category-input"/>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Reorder level</label>
        <Input type="number" min="0" step="0.001" value={form.reorder_level} onChange={e => set("reorder_level", e.target.value)} data-testid="stock-item-reorder-input"/>
        <p className="text-micro text-muted-foreground">At or below this level the item is flagged for reorder.</p>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Usable %</label>
        <Input type="number" min="1" max="100" step="0.5" value={form.usable_pct} onChange={e => set("usable_pct", e.target.value)} data-testid="stock-item-usable-input"/>
        <p className="text-micro text-muted-foreground">How much of what you buy is usable after cleaning or trimming — 1 kg chicken giving 800 g is 80. Recipes take the usable amount; stock comes off as bought.</p>
      </div>
      {item && (
        <label className="flex items-center gap-snug text-body font-semibold mt-loose">
          <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary" checked={form.is_active} onChange={e => set("is_active", e.target.checked)} data-testid="stock-item-active-checkbox"/>
          Active
        </label>
      )}
      {item && <BuyUnitsEditor item={item} token={token} canWrite={canWrite} />}
      {failure && <p className="text-body font-semibold text-destructive mt-snug" data-testid="stock-item-error">{failure}</p>}
    </div>
  </Modal>;
}

function MovementDialog({ stock, onSave, onClose }) {
  const [form, setForm] = useState({
    itemId: "",
    type: "purchase",
    direction: "1",
    magnitude: "",
    unitCost: "",
    notes: "",
    // The day it happened (owner brief B7); a past day needs "enter on a past date".
    date: new Date().toLocaleDateString("en-CA"),
  });
  const [failure, setFailure] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));

  const item = stock.find(row => String(row.id) === String(form.itemId)) || null;
  const needsDirection = MOVEMENT_DIRECTION[form.type] === 0;
  const signed = signedQuantity(form.type, form.magnitude, needsDirection ? Number(form.direction) : null);

  const submit = async () => {
    if (!item) return toast.error("Choose a stock item");
    if (signed === null) return toast.error("Enter a quantity greater than zero");
    setSaving(true);
    const result = await onSave({
      stock_item_id: item.id,
      movement_type: form.type,
      quantity: signed,
      unit_cost: form.unitCost === "" ? null : Number(form.unitCost),
      notes: form.notes.trim() || null,
      effective_date: form.date || null,
    });
    setSaving(false);
    if (!result.ok) {
      setFailure(result.error);
      return toast.error(result.error);
    }
    toast.success(`${signed > 0 ? "+" : "−"}${qty(Math.abs(signed))} ${item.unit} · ${item.name}`);
    onClose();
  };

  return <Modal title="Record stock movement" onClose={onClose} testId="stock-movement-modal" footer={<><Button variant="outline" onClick={onClose} data-testid="save-movement-button-cancel">Cancel</Button><Button variant="action" onClick={submit} disabled={saving} data-testid="save-movement-button">Record movement</Button></>}>
    <div className="space-y-4">
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Item</label>
        <select className="w-full h-9 px-3 border rounded-md bg-card focus:outline-none focus:ring-2 focus:ring-primary/50 text-body" value={form.itemId} onChange={e => set("itemId", e.target.value)} data-testid="movement-item-select">
          <option value="">Choose an item…</option>
          {stock.map(row => (
            <option value={row.id} key={row.id}>{row.name} · {qty(row.on_hand)} {row.unit} on hand</option>
          ))}
        </select>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Movement type</label>
        <select className="w-full h-9 px-3 border rounded-md bg-card focus:outline-none focus:ring-2 focus:ring-primary/50 text-body" value={form.type} onChange={e => set("type", e.target.value)} data-testid="movement-type-select">
          {MOVEMENT_TYPES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
        </select>
      </div>
      {needsDirection && (
        <div className="space-y-1.5">
          <label className="text-meta font-medium text-muted-foreground">Direction</label>
          <select className="w-full h-9 px-3 border rounded-md bg-card focus:outline-none focus:ring-2 focus:ring-primary/50 text-body" value={form.direction} onChange={e => set("direction", e.target.value)} data-testid="movement-direction-select">
            <option value="1">Add to stock — a count found more</option>
            <option value="-1">Remove from stock — a count found less</option>
          </select>
        </div>
      )}
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Quantity{item ? ` (${item.unit})` : ""}</label>
        <Input type="number" min="0" step="0.001" value={form.magnitude} onChange={e => set("magnitude", e.target.value)} data-testid="movement-quantity-input"/>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Unit cost</label>
        <Input type="number" min="0" step="0.01" value={form.unitCost} onChange={e => set("unitCost", e.target.value)} placeholder="optional, rupees" data-testid="movement-unit-cost-input"/>
        <p className="text-micro text-muted-foreground">Per {item ? item.unit : "stock unit"}. Stock is valued at the average cost of purchases, openings and counts.</p>
      </div>
      <div className="space-y-1.5">
        <label className="block text-meta font-medium text-muted-foreground">Date</label>
        <DatePicker value={form.date} onChange={v => set("date", v)} ariaLabel="Movement date" testId="movement-date-input"/>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Note</label>
        <Input value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Spoiled in the walk-in" data-testid="movement-notes-input"/>
      </div>
      
      <div className="bg-muted p-base rounded-lg text-body mt-loose" data-testid="movement-effect">
        {item && signed !== null
          ? <span className="font-medium text-foreground">{signed > 0 ? "Adds" : "Removes"} {qty(Math.abs(signed))} {item.unit}. {item.name} goes from {qty(item.on_hand)} to {qty(Number(item.on_hand) + signed)} {item.unit}.</span>
          : <span className="text-muted-foreground">Choose an item, a movement type and a quantity to see what this will do.</span>}
      </div>
      
      {failure && <p className="text-body font-semibold text-destructive mt-snug" data-testid="movement-error">{failure}</p>}
    </div>
  </Modal>;
}

/**
 * The dense row shape the rest of the product now copies: a 12px uppercase column
 * header, 13px cells, one vertical padding step, and every number in a
 * `tabular-nums` column so a stock figure can be compared down the page by length.
 */
function StockTable({ rows, canWrite, onEdit, testId }) {
  return (
    <div className="rounded-xl border bg-card overflow-hidden shadow-sm" data-testid={testId}>
      <div className="overflow-x-auto scroll-contain-x">
        <table className="w-full text-body text-left">
          <thead className="text-meta text-muted-foreground bg-muted/50">
            <tr>
              <th className="px-base py-snug font-medium">Item</th>
              <th className="px-base py-snug font-medium hidden sm:table-cell">Category</th>
              <th className="px-base py-snug font-medium text-right whitespace-nowrap">On hand</th>
              <th className="px-base py-snug font-medium text-right">Reorder level</th>
              <th className="px-base py-snug font-medium text-left">Status</th>
              <th className="px-base py-snug font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((row, index) => (
              <tr key={row.id} className="hover:bg-muted/30 transition-colors" data-testid={`${testId}-row-${index}`}>
                <td className="px-base py-snug">
                  <div className="flex items-center gap-snug">
                    <div className="flex flex-col">
                      <strong className="font-semibold text-foreground">{row.name}</strong>
                      <small className="text-meta text-muted-foreground">{row.sku || "no SKU"}{row.is_active === false ? " · inactive" : ""}</small>
                    </div>
                  </div>
                </td>
                <td className="px-base py-snug text-muted-foreground hidden sm:table-cell">{row.category || "—"}</td>
                <td className="px-base py-snug text-right font-medium tabular-nums whitespace-nowrap" data-testid={`${testId}-row-${index}-on-hand`}>{qty(row.on_hand)} <span className="text-muted-foreground text-meta ml-hair">{row.unit}</span></td>
                <td className="px-base py-snug text-right text-muted-foreground tabular-nums">{qty(row.reorder_level)} <span className="text-meta ml-hair">{row.unit}</span></td>
                <td className="px-base py-snug">
                  {/* DESIGN.md §5.6: status is a dot and a word, not a filled pill. */}
                  <span className="inline-flex items-center gap-1.5 text-body whitespace-nowrap">
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${row.is_low ? "bg-danger" : "bg-success"}`} aria-hidden="true" />
                    <span className={row.is_low ? "text-danger" : "text-foreground"}>{row.is_low ? "Reorder" : "OK"}</span>
                  </span>
                </td>
                <td className="px-base py-snug text-right">
                  {canWrite ? (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => onEdit(row)} data-testid={`${testId}-row-${index}-edit-button`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function InventoryPage() {
  const { user, token, permissions } = usePOS();
  // Per-person switches (2026-10-20). Read-only stock = see without add/edit. The
  // valuation is a report of its own. Old cached session: role lists (lib/access.js).
  const canRead = screenAllowed("inventory", user, permissions, READ_ROLES);
  const canWrite = featureAllowed("inventory.edit", user, permissions, WRITE_ROLES.includes(user?.role));
  const canValue = featureAllowed("report.inventory-valuation", user, permissions, WRITE_ROLES.includes(user?.role));
  const inventory = useInventory(canRead ? token : null, { canValue });
  const [tab, setTab] = useState("stock");
  const [dialog, setDialog] = useState(null);
  const [stores, setStores] = useState([]);
  const [storeFilter, setStoreFilter] = useState("");
  useEffect(() => {
    if (!canRead || !token) return undefined;
    let stale = false;
    listStores(token).then(list => { if (!stale) setStores(list); }).catch(() => { if (!stale) setStores([]); });
    return () => { stale = true; };
  }, [canRead, token]);
  const shownStock = storeFilter
    ? inventory.stock.filter(r => String(r.department_id || "") === storeFilter)
    : inventory.stock;

  const tabs = [
    ["stock", "Stock levels", Boxes],
    ["low", "Reorder list", TriangleAlert],
    ["ledger", "Movement ledger", ScrollText],
    ["counts", "Counts", ClipboardList],
    ...(canValue ? [["valuation", "Valuation", IndianRupee]] : []),
    ["recipes", "Recipes", ChefHat],
  ];

  const itemName = useMemo(() => {
    const names = {};
    for (const item of inventory.items) names[item.id] = item;
    return names;
  }, [inventory.items]);

  const setFilter = (key, value) =>
    inventory.setFilters(current => ({ ...current, [key]: value || null }));

  if (!canRead) {
    return (
      <PageShell testId="inventory-management-page">
        <EmptyState icon={LockKeyhole} title="Access Denied" message="Stock levels are visible to admin and cashier accounts." testId="inventory-forbidden"/>
      </PageShell>
    );
  }

  return (
    /*
     * The caption is the one line of the old hero that earned its place: on hand is
     * a sum over the movement ledger, never a stored running total, and a manager
     * reading a figure here has to know that before they trust it (§47, and the
     * project's "running totals are derived from their ledger" rule). INVENTORY
     * CONTROL / "Stock & Movements" said nothing the topbar had not already said.
     */
    <PageShell
      testId="inventory-management-page"
      caption="Stock on hand, worked out from every recorded movement."
      actions={canWrite && (
        <>
          <Button variant="outline" size="sm" className="font-semibold shadow-sm" onClick={() => setDialog({ kind: "movement" })} data-testid="record-movement-button">
            <ArrowDownUp className="w-4 h-4 mr-snug"/>Record movement
          </Button>
          <Button variant="action" size="sm" className="font-semibold shadow-sm" onClick={() => setDialog({ kind: "item" })} data-testid="add-stock-item-button">
            <Plus className="w-4 h-4 mr-snug"/>Add stock item
          </Button>
        </>
      )}
    >
      {inventory.error && (
        <div className="bg-destructive/10 text-destructive border border-destructive/20 p-base rounded-lg flex items-start gap-snug shrink-0" role="alert" data-testid="inventory-error">
          <TriangleAlert className="w-4 h-4 shrink-0 mt-hair"/>
          <span className="text-body font-semibold">{inventory.error}</span>
        </div>
      )}

      {/*
        * Four numbers, one bordered strip — not four rounded cards each spending 16px
        * of padding and a 30px figure on a count that is usually single-digit (§2.2:
        * no giant cards). The strip divides rather than floats, so the numbers line
        * up on one baseline and read as one row of facts about the same catalogue.
        */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border rounded-lg bg-card divide-y sm:divide-y-0 sm:divide-x lg:divide-y-0 shrink-0" data-testid="inventory-metrics">
        <div className="p-base flex flex-col gap-hair">
          <small className="text-meta font-medium text-muted-foreground">Items tracked</small>
          <strong className="text-lede font-semibold text-foreground tabular-nums" data-testid="metric-items-tracked">{inventory.stock.length}</strong>
          <b className="text-meta font-normal text-muted-foreground">{inventory.activeOnly ? "active items" : "including inactive"}</b>
        </div>
        <div className="p-base flex flex-col gap-hair">
          <small className="text-meta font-medium text-muted-foreground">At or below reorder</small>
          <strong className={`text-lede font-semibold tabular-nums ${inventory.low.length ? 'text-danger' : 'text-foreground'}`} data-testid="metric-below-reorder">{inventory.low.length}</strong>
          <b className="text-meta font-normal text-muted-foreground">need restocking</b>
        </div>
        <div className="p-base flex flex-col gap-hair">
          <small className="text-meta font-medium text-muted-foreground">Recent movements</small>
          <strong className="text-lede font-semibold text-foreground tabular-nums" data-testid="metric-ledger-rows">{inventory.movements.length}</strong>
          <b className="text-meta font-normal text-muted-foreground">newest 100 shown</b>
        </div>
        {canWrite && (
          <div className="p-base flex flex-col gap-hair">
            <small className="text-meta font-medium text-muted-foreground">Stock value</small>
            <strong className={MONEY_GRAND_TOTAL} data-testid="metric-stock-value">{inventory.valuation ? inr(inventory.valuation.total_value) : "—"}</strong>
            <b className="text-meta font-normal text-muted-foreground">at average purchase cost</b>
          </div>
        )}
      </div>

      <div className="flex gap-snug overflow-x-auto scrollbar-hide border-b shrink-0" data-testid="module-tabs">
        {tabs.map(([id, label, Icon]) => {
          const isActive = tab === id;
          return (
            <button
              key={id}
              className={`flex shrink-0 items-center gap-snug whitespace-nowrap px-base py-snug -mb-px text-body font-medium transition-colors border-b-2 ${isActive ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              onClick={() => setTab(id)}
              data-testid={`inventory-tab-${id}-button`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          )
        })}
      </div>

      {tab === "stock" && <div className="space-y-base" data-testid="stock-levels-panel">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-snug">
          <div>
            <h3 className="text-title font-semibold text-foreground">Stock levels</h3>
            <p className="text-meta text-muted-foreground">{inventory.stock.length} item(s){inventory.low.length ? ` · ${inventory.low.length} at or below reorder` : ""}</p>
          </div>
          {stores.length > 0 && (
            <select className="h-11 px-snug border rounded-md bg-card text-body" value={storeFilter}
                    onChange={e => setStoreFilter(e.target.value)} data-testid="stock-store-filter">
              <option value="">Every store</option>
              {stores.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          <label className="flex items-center gap-snug text-meta font-semibold text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              checked={!inventory.activeOnly}
              onChange={e => inventory.setActiveOnly(!e.target.checked)}
              data-testid="show-inactive-items-checkbox"
            />
            Show inactive items
          </label>
        </div>
        {shownStock.length > 0 && (
          <StockTable rows={shownStock} canWrite={canWrite} onEdit={item => setDialog({ kind: "item", item })} testId="stock-levels-table"/>
        )}
        {!inventory.stock.length && inventory.loading && (
          <LoadingState message="Reading stock levels…" testId="stock-loading" />
        )}
        {!inventory.stock.length && !inventory.loading && (
          inventory.loaded
            ? <EmptyState icon={Boxes} title="Empty Catalogue" message="The stock catalogue is empty. Add an item to start tracking it." testId="stock-empty" />
            : <EmptyState icon={TriangleAlert} title="Error" message="Stock levels could not be read from the server." testId="stock-unreadable" />
        )}
      </div>}

      {tab === "low" && <div className="space-y-base" data-testid="low-stock-panel">
        <div>
          <h3 className="text-title font-semibold text-foreground">Reorder list</h3>
          <p className="text-meta text-muted-foreground">Items whose on-hand is at or below their reorder level.</p>
        </div>
        {inventory.low.length > 0 && (
          <StockTable rows={inventory.low} canWrite={canWrite} onEdit={item => setDialog({ kind: "item", item })} testId="low-stock-table"/>
        )}
        {!inventory.low.length && inventory.loading && (
          <LoadingState message="Reading the reorder list…" testId="low-stock-loading" />
        )}
        {!inventory.low.length && !inventory.loading && (
          <EmptyState
            icon={inventory.loaded ? CheckCircle2 : TriangleAlert}
            title={inventory.loaded ? "All Good" : "Error"}
            message={inventory.loaded ? "Nothing is at or below its reorder level." : "The reorder list could not be read from the server."}
            testId={inventory.loaded ? "low-stock-empty" : "low-stock-unreadable"}
          />
        )}
      </div>}

      {tab === "ledger" && <div className="space-y-base" data-testid="movement-ledger-panel">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-base bg-muted/30 p-base rounded-lg border">
          <div className="space-y-tight">
            <label className="block text-meta font-medium text-muted-foreground">Item</label>
            <select className="w-full h-9 px-snug border rounded-md bg-card text-body" value={inventory.filters.stockItemId || ""} onChange={e => setFilter("stockItemId", e.target.value)} data-testid="ledger-item-filter">
              <option value="">All items</option>
              {inventory.items.map(item => <option value={item.id} key={item.id}>{item.name}</option>)}
            </select>
          </div>
          <div className="space-y-tight">
            <label className="block text-meta font-medium text-muted-foreground">Type</label>
            <select className="w-full h-9 px-snug border rounded-md bg-card text-body" value={inventory.filters.movementType || ""} onChange={e => setFilter("movementType", e.target.value)} data-testid="ledger-type-filter">
              <option value="">All movements</option>
              {MOVEMENT_TYPES.map(([value]) => <option value={value} key={value}>{typeLabel(value)}</option>)}
            </select>
          </div>
          <div className="space-y-tight">
            <label className="block text-meta font-medium text-muted-foreground">From</label>
            <DatePicker value={inventory.filters.dateFrom || ""} max={inventory.filters.dateTo || undefined} onChange={next => setFilter("dateFrom", next)} ariaLabel="From" testId="ledger-from-filter"/>
          </div>
          <div className="space-y-tight">
            <label className="block text-meta font-medium text-muted-foreground">To</label>
            <DatePicker value={inventory.filters.dateTo || ""} min={inventory.filters.dateFrom || undefined} onChange={next => setFilter("dateTo", next)} ariaLabel="To" testId="ledger-to-filter"/>
          </div>
        </div>

        {inventory.movements.length > 0 && (
          <div className="rounded-xl border bg-card overflow-hidden shadow-sm" data-testid="movement-ledger-table">
            <div className="overflow-x-auto scroll-contain-x">
              <table className="w-full text-body text-left">
                <thead className="text-meta text-muted-foreground bg-muted/50">
                  <tr>
                    <th className="px-base py-snug font-medium">When</th>
                    <th className="px-base py-snug font-medium">Item</th>
                    <th className="px-base py-snug font-medium">Type</th>
                    <th className="px-base py-snug font-medium text-right">Quantity</th>
                    <th className="px-base py-snug font-medium text-right">Unit cost</th>
                    <th className="px-base py-snug font-medium">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {inventory.movements.map((movement, index) => {
                    const item = itemName[movement.stock_item_id];
                    return (
                      <tr key={movement.id} className="hover:bg-muted/30 transition-colors" data-testid={`movement-row-${index}`}>
                        <td className="px-base py-snug text-muted-foreground whitespace-nowrap tabular-nums">{stamp(movement.created_at)}</td>
                        <td className="px-base py-snug font-semibold">{item?.name || `#${movement.stock_item_id}`}</td>
                        <td className="px-base py-snug"><span className="bg-muted px-snug py-hair rounded-md text-meta font-semibold">{typeLabel(movement.movement_type)}</span></td>
                        <td className="px-base py-snug text-right">
                          <span
                            className={`font-bold tabular-nums ${Number(movement.quantity) < 0 ? "text-danger" : "text-success"}`}
                            data-testid={`movement-row-${index}-quantity`}
                          >
                            {Number(movement.quantity) > 0 ? "+" : "−"}{qty(Math.abs(movement.quantity))} <span className="text-meta ml-hair opacity-70">{item?.unit || ""}</span>
                          </span>
                        </td>
                        <td className={`px-base py-snug text-right ${MONEY_LINE}`}>{movement.unit_cost === null || movement.unit_cost === undefined ? "—" : inr(movement.unit_cost)}</td>
                        <td className="px-base py-snug text-muted-foreground text-meta">{movement.notes || (movement.reference_type ? `${typeLabel(movement.reference_type)} #${movement.reference_id}` : "—")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {!inventory.movements.length && inventory.loading && (
          <LoadingState message="Reading the ledger…" testId="ledger-loading" />
        )}
        {!inventory.movements.length && !inventory.loading && (
          <EmptyState icon={ScrollText} title="No movements" message="No movements match these filters." testId="ledger-empty" />
        )}
      </div>}

      {tab === "counts" && <StockCounts token={token} stores={stores} canWrite={canWrite} />}
      {tab === "recipes" && <RecipesPanel token={token} stockItems={inventory.items} canWrite={canWrite} />}
      {tab === "valuation" && canValue && <div className="space-y-base" data-testid="valuation-panel">
        {inventory.valuation ? <>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-snug bg-muted/20 px-base py-snug rounded-lg border">
            <div>
              <h3 className="text-title font-semibold text-foreground">Stock valuation</h3>
              <p className="text-meta text-muted-foreground">
                Method: <span className="font-semibold text-foreground">{inventory.valuation.method}</span>. <br className="sm:hidden" />
                <span className="font-bold text-foreground tabular-nums">{inventory.valuation.items_without_purchase_history}</span> item(s) have never been purchased through this module and therefore value at zero.
              </p>
            </div>
            <strong className={`${MONEY_GRAND_TOTAL} text-primary shrink-0`} data-testid="valuation-total">{inr(inventory.valuation.total_value)}</strong>
          </div>

          <div className="rounded-xl border bg-card overflow-hidden shadow-sm" data-testid="valuation-table">
            <div className="overflow-x-auto">
              <table className="w-full text-body text-left">
                <thead className="text-meta text-muted-foreground bg-muted/50">
                  <tr>
                    <th className="px-base py-snug font-medium">Item</th>
                    <th className="px-base py-snug font-medium">Unit</th>
                    <th className="px-base py-snug font-medium text-right">On hand</th>
                    <th className="px-base py-snug font-medium text-right">Avg unit cost</th>
                    <th className="px-base py-snug font-medium text-right">Value</th>
                    <th className="px-base py-snug font-medium">Costing</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {inventory.valuation.lines.map((line, index) => (
                    <tr key={line.id} className="hover:bg-muted/30 transition-colors" data-testid={`valuation-row-${index}`}>
                      <td className="px-base py-snug font-semibold text-foreground">{line.name}</td>
                      <td className="px-base py-snug text-muted-foreground text-meta">{line.unit}</td>
                      <td className="px-base py-snug text-right font-medium tabular-nums">{qty(line.on_hand)}</td>
                      <td className={`px-base py-snug text-right ${MONEY_CAPTION}`}>{inr(line.avg_unit_cost)}</td>
                      <td className={`px-base py-snug text-right ${MONEY_LINE} text-primary`} data-testid={`valuation-row-${index}-value`}>{inr(line.value)}</td>
                      <td className="px-base py-snug text-meta text-muted-foreground">
                        {Number(line.avg_unit_cost) > 0
                          ? <span className="bg-success-subtle text-success px-snug py-hair rounded-md text-meta font-medium">Purchase history</span>
                          : <span className="bg-danger-subtle text-danger px-snug py-hair rounded-md text-meta font-medium">Never purchased</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {!inventory.valuation.lines.length && (
            <EmptyState icon={TrendingDown} title="Nothing to value" message="The catalogue is empty." testId="valuation-empty" />
          )}
        </> : (
          inventory.loading
            ? <LoadingState message="Reading the valuation…" testId="valuation-loading" />
            : <EmptyState
                icon={TriangleAlert}
                title="Error"
                message="The valuation could not be read from the server."
                testId="valuation-unavailable"
              />
        )}
      </div>}

      {dialog?.kind === "item" && (
        <StockItemDialog
          item={dialog.item}
          stores={stores}
          token={token}
          canWrite={canWrite}
          onSave={body => (dialog.item ? inventory.editItem(dialog.item.id, body) : inventory.addItem(body))}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "movement" && (
        <MovementDialog
          stock={inventory.stock}
          onSave={inventory.recordMovement}
          onClose={() => setDialog(null)}
        />
      )}
    </PageShell>
  );
}
